import { useTranslation } from 'react-i18next'
import { useSEO } from './useSEO'
import type { BreadcrumbItem } from '@/lib/breadcrumbs'

type SEOOverrides = {
    canonicalPath?: string
    noIndex?: boolean
    ogImage?: string
    ogType?: 'website' | 'article'
    jsonLd?: Record<string, unknown>
    breadcrumbs?: BreadcrumbItem[]
}

/**
 * Page metadata sourced from the active locale.
 *
 * useSEO already emits a self-referential canonical and a full hreflang set per
 * '?lng=' URL. Those URLs are only worth indexing if what they serve actually
 * differs, so title/description have to come from t() rather than from a
 * literal baked into the component.
 *
 * Optional fields are omitted rather than passed as the raw key: i18next
 * returns the key itself when a lookup misses, and 'seo.pricing.keywords'
 * inside a <meta> tag is worse than no tag at all.
 */
export function usePageSEO(page: string, overrides: SEOOverrides = {}) {
    const { t } = useTranslation()

    const optional = (suffix: string): string | undefined => {
        const key = `seo.${page}.${suffix}`
        const value = t(key)
        return value === key ? undefined : value
    }

    useSEO({
        title: t(`seo.${page}.title`),
        description: t(`seo.${page}.description`),
        keywords: optional('keywords'),
        ogTitle: optional('ogTitle'),
        ogDescription: optional('ogDescription'),
        ...overrides,
    })
}
